/**
 * ConsentModal Component
 *
 * Cookie consent popup with wedding theme.
 */

import { useState, useEffect, useCallback } from 'react';
import { useConsent } from './useConsent';
import { ConsentService } from './ConsentService';
import styles from './Consent.module.css';

type ModalView = 'main' | 'details';

export function ConsentModal() {
  const {
    config,
    hasConsent,
    choices,
    acceptAll,
    rejectAll,
    saveCustom,
    toggleService,
  } = useConsent();

  const [isOpen, setIsOpen] = useState(!hasConsent);
  const [view, setView] = useState<ModalView>('main');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsOpen(true);
    setView('main');
  }, []);

  // Listen for clicks on #preference-cookies element in footer
  useEffect(() => {
    const trigger = document.getElementById('preference-cookies');
    if (trigger) {
      trigger.addEventListener('click', handleOpenModal);
      return () => trigger.removeEventListener('click', handleOpenModal);
    }
  }, [handleOpenModal]);

  if (!config) return null;

  const services = Object.values(config.services);
  const optionalServices = services.filter((s) => !s.required);

  const handleAcceptAll = async () => {
    setIsSubmitting(true);
    await acceptAll();
    setIsSubmitting(false);
    setIsOpen(false);
  };

  const handleRejectAll = async () => {
    setIsSubmitting(true);
    await rejectAll();
    setIsSubmitting(false);
    setIsOpen(false);
  };

  const handleSaveCustom = async () => {
    setIsSubmitting(true);
    await saveCustom();
    setIsSubmitting(false);
    setIsOpen(false);
    setView('main');
  };

  // Don't show anything if modal is closed
  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        {view === 'main' ? (
          <>
            <div className={styles.header}>
              <h3 className={styles.title}>Nous respectons votre vie privée</h3>
              <p className={styles.description}>
                Nous utilisons des cookies pour améliorer votre expérience. Choisissez les services que vous acceptez.
              </p>
            </div>

            <div className={styles.links}>
              <button
                type="button"
                className={styles.link}
                onClick={() => setView('details')}
              >
                Personnaliser mes choix
              </button>
              <a
                href="/politique-de-confidentialite"
                className={styles.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Plus d'informations
              </a>
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleRejectAll}
                disabled={isSubmitting}
              >
                Je m'y oppose
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleAcceptAll}
                disabled={isSubmitting}
              >
                Je le veux
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.header}>
              <button
                type="button"
                className={styles.backButton}
                onClick={() => setView('main')}
                aria-label="Retour"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className={styles.title}>Vos préférences</h3>
            </div>

            <div className={styles.servicesList}>
              {/* Required services */}
              <div className={styles.categorySection}>
                <h4 className={styles.categoryTitle}>Nécessaires</h4>
                <p className={styles.categoryDescription}>
                  Ces services sont essentiels au fonctionnement du site.
                </p>
                {services
                  .filter((s) => s.required)
                  .map((service) => (
                    <ConsentService
                      key={service.id}
                      service={service}
                      checked={true}
                      onChange={() => {}}
                    />
                  ))}
              </div>

              {/* Optional services */}
              {optionalServices.length > 0 && (
                <div className={styles.categorySection}>
                  <h4 className={styles.categoryTitle}>Mesure d'audience</h4>
                  <p className={styles.categoryDescription}>
                    Ces services nous aident à comprendre votre navigation.
                  </p>
                  {optionalServices.map((service) => (
                    <ConsentService
                      key={service.id}
                      service={service}
                      checked={choices[service.id] || false}
                      onChange={() => toggleService(service.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSaveCustom}
                disabled={isSubmitting}
              >
                Confirmer mes choix
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
